import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Edit2, Save, X } from "lucide-react";
import { pb } from "@/lib/pocketbase";
import { toast } from "sonner";
import { detectTextDirection, getTextAlign } from "@/utils/textDirection";

interface LearningPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutorId: string;
  isOwnProfile: boolean;
  tutorAbout?: string;
}

export const LearningPlanModal = ({ isOpen, onClose, tutorId, isOwnProfile, tutorAbout }: LearningPlanModalProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [header, setHeader] = useState("");
  const [content, setContent] = useState("");
  const [existingPlanId, setExistingPlanId] = useState<string | null>(null);
  const [hasPlan, setHasPlan] = useState(false);

  useEffect(() => {
    if (isOpen && tutorId) {
      fetchLearningPlan();
    }
  }, [isOpen, tutorId]);

  const fetchLearningPlan = async () => {
    try {
      setIsLoading(true);
      const records = await pb.collection('tutor_learning_plans').getList(1, 1, {
        filter: `tutorId = "${tutorId}"`,
      });

      if (records.items.length > 0) {
        const plan = records.items[0];
        setHeader(plan.header || "");
        setContent(plan.content || "");
        setExistingPlanId(plan.id);
        setHasPlan(true);
      } else {
        setHasPlan(false);
      }
    } catch (error) {
      console.error("Error fetching learning plan:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!header.trim() || !content.trim()) {
      toast.error("Please fill in both header and content");
      return;
    }

    try {
      setIsLoading(true);
      
      const data = {
        tutorId,
        header: header.trim(),
        content: content.trim(),
      };

      console.log("Attempting to save learning plan:", data);
      console.log("User auth:", pb.authStore.model);

      if (existingPlanId) {
        await pb.collection('tutor_learning_plans').update(existingPlanId, data);
        toast.success("Learning plan updated successfully");
      } else {
        const record = await pb.collection('tutor_learning_plans').create(data);
        setExistingPlanId(record.id);
        toast.success("Learning plan created successfully");
      }
      
      setHasPlan(true);
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error saving learning plan:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.status,
        response: error.response,
        data: error.data
      });
      toast.error(`Failed to save learning plan: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (!hasPlan) {
      setHeader("");
      setContent("");
    } else {
      fetchLearningPlan();
    }
    setIsEditing(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span>Learning Plan</span>
              <span className="text-muted-foreground">•</span>
              <span dir="rtl">خطة التعلم</span>
            </div>
            {isOwnProfile && hasPlan && !isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="gap-2"
              >
                <Edit2 className="h-4 w-4" />
                Edit
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading && !isEditing ? (
          <div className="text-center py-8">Loading...</div>
        ) : !hasPlan && !isEditing ? (
          <div className="py-8">
            <h3 className="text-lg font-semibold mb-4">About</h3>
            <p 
              className="text-foreground whitespace-pre-wrap mb-6"
              style={{ unicodeBidi: 'plaintext', textAlign: 'start' }}
            >
              {tutorAbout || "No information available yet."}
            </p>
            {isOwnProfile && (
              <Button onClick={() => setIsEditing(true)} className="mt-6">Create Learning Plan</Button>
            )}
          </div>
        ) : isEditing || !hasPlan ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="header">Header</Label>
              <Input
                id="header"
                value={header}
                onChange={(e) => setHeader(e.target.value)}
                placeholder="Enter a catchy header for your learning plan"
                style={{ unicodeBidi: 'plaintext', textAlign: 'start' }}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Describe your teaching methodology, learning approach, and what students can expect..."
                rows={8}
                style={{ unicodeBidi: 'plaintext', textAlign: 'start' }}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isLoading}>
                <Save className="h-4 w-4 mr-2" />
                {isLoading ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h3 
                className="text-lg font-semibold mb-2"
                style={{ unicodeBidi: 'plaintext', textAlign: 'start' }}
              >
                {header}
              </h3>
              <p 
                className="text-foreground whitespace-pre-wrap"
                style={{ unicodeBidi: 'plaintext', textAlign: 'start' }}
              >
                {content}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
